import { useEffect, useRef, useState } from 'react'
import { useArenaStore } from '../store'

const CONTROLS_HINT_MS = 8000

export function HUD() {
  const health          = useArenaStore((s) => s.health)
  const runs            = useArenaStore((s) => s.runs)
  const queue           = useArenaStore((s) => s.queue)
  const focusedRunId    = useArenaStore((s) => s.focusedRunId)
  const setFocusedRunId = useArenaStore((s) => s.setFocusedRunId)
  const isPlaying       = useArenaStore((s) => s.isPlaying)

  const [controlsHintVisible, setControlsHintVisible] = useState(false)
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Show controls hint for CONTROLS_HINT_MS whenever play starts
  useEffect(() => {
    if (isPlaying) {
      setControlsHintVisible(true)
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current)
      hintTimerRef.current = setTimeout(() => setControlsHintVisible(false), CONTROLS_HINT_MS)
    } else {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current)
      setControlsHintVisible(false)
    }
    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current)
    }
  }, [isPlaying])

  const focusedRun = runs.find((r) => r.id === focusedRunId)
  // Count the runs we actually render — the daemon's activeRuns/active_agents
  // counts kernel agent slots, not delegated node runs, so it reads 0 while
  // desks are visibly working.
  const activeCount = runs.filter((r) => r.status === 'running').length
  const queueCount  = health?.queueDepth ?? queue.length
  const daemonStatus = health?.status ?? null
  const dotColor = daemonStatus === 'healthy' ? '#a3b18a'
                 : daemonStatus === null       ? 'rgba(245,235,224,0.3)'
                 : '#f87171'

  if (!isPlaying) return null

  return (
    <>
      {/* Top-left fleet chip */}
      <div style={s.fleetChip}>
        <span style={{ ...s.statusDot, color: dotColor }}>●</span>
        <span style={s.fleetText}>
          {daemonStatus ?? 'unknown'} · {activeCount} active · {queueCount} queued
        </span>
      </div>

      {/* Bottom-center controls hint — auto-fades after 8s */}
      {controlsHintVisible && (
        <div style={s.controlsHint} data-testid="controls-hint">
          <span style={s.keycap}>WASD</span> Move &nbsp;
          <span style={s.keycap}>Mouse</span> Look &nbsp;
          <span style={s.keycap}>E</span> Interact &nbsp;
          <span style={s.keycap}>Esc</span> Pause
        </div>
      )}

      {/* Bottom panel — focused run */}
      {focusedRun && (
        <div style={s.bottomPanel}>
          <div style={s.runHeader}>
            <span style={s.runTitle}>{focusedRun.title}</span>
            <span style={{ ...s.statusBadge, ...statusBadgeColor(focusedRun.status) }}>
              {focusedRun.status}
            </span>
            <button style={s.closeBtn} onClick={() => setFocusedRunId(null)}>✕</button>
          </div>
          <div style={s.phases}>
            {focusedRun.phases.map((phase) => (
              <div key={phase.id} style={s.phase}>
                <span style={{ ...s.phaseDot, background: phaseColor(phase.status) }} />
                <span style={s.phaseTitle}>{phase.title}</span>
                <span style={s.phaseStatus}>{phase.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function statusBadgeColor(status: string): React.CSSProperties {
  switch (status) {
    case 'running':  return { color: '#a78bfa', borderColor: 'rgba(167,139,250,0.4)' }
    case 'done':     return { color: '#22d3ee', borderColor: 'rgba(34,211,238,0.4)' }
    case 'failed':   return { color: '#f87171', borderColor: 'rgba(248,113,113,0.4)' }
    default:         return { color: '#fbbf24', borderColor: 'rgba(251,191,36,0.4)' }
  }
}

function phaseColor(status: string): string {
  switch (status) {
    case 'completed': return '#22d3ee'
    case 'running':   return '#a78bfa'
    default:          return '#475569'
  }
}

const s: Record<string, React.CSSProperties> = {
  fleetChip: {
    position: 'fixed',
    top: '1rem',
    left: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.35rem 0.75rem',
    background: 'rgba(61,64,91,0.75)',
    border: '1px solid rgba(245,235,224,0.12)',
    borderRadius: '9999px',
    backdropFilter: 'blur(8px)',
    zIndex: 15,
  },
  statusDot: {
    fontSize: '0.55rem',
    lineHeight: 1,
  },
  fleetText: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.78rem',
    fontWeight: 700,
    color: 'rgba(245,235,224,0.85)',
    letterSpacing: '0.02em',
  },
  controlsHint: {
    position: 'fixed',
    bottom: '1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.45rem 1rem',
    background: 'rgba(61,64,91,0.75)',
    border: '1px solid rgba(245,235,224,0.1)',
    borderRadius: '9999px',
    backdropFilter: 'blur(8px)',
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.76rem',
    color: 'rgba(245,235,224,0.55)',
    zIndex: 15,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  keycap: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.68rem',
    fontWeight: 700,
    padding: '0.1rem 0.35rem',
    background: 'rgba(245,235,224,0.1)',
    border: '1px solid rgba(245,235,224,0.2)',
    borderBottom: '2px solid rgba(245,235,224,0.1)',
    borderRadius: '0.25rem',
    color: 'rgba(245,235,224,0.8)',
  },
  bottomPanel: {
    position: 'fixed',
    bottom: '4rem',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(61,64,91,0.88)',
    border: '1px solid rgba(245,235,224,0.12)',
    borderRadius: '0.875rem',
    padding: '1rem 1.25rem',
    minWidth: '22rem',
    maxWidth: '36rem',
    zIndex: 15,
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  },
  runHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.75rem',
    gap: '0.5rem',
  },
  runTitle: {
    flex: 1,
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: '0.938rem',
    color: '#f5ebe0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    border: '1px solid',
    flexShrink: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(245,235,224,0.35)',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0 0.25rem',
    flexShrink: 0,
  },
  phases: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  },
  phase: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.8rem',
  },
  phaseDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  phaseTitle: {
    flex: 1,
    fontFamily: "'Nunito', system-ui, sans-serif",
    color: 'rgba(245,235,224,0.75)',
  },
  phaseStatus: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    color: 'rgba(245,235,224,0.4)',
    fontSize: '0.75rem',
  },
}
